import type { Directive } from 'vue'

// v-focus-trap — keeps Tab / Shift+Tab cycling inside the host element
// for as long as it's mounted. Apply to any container that should keep
// keyboard focus inside while it's visible: modals, sheets, command
// palettes, any "modeless" surface that traps interaction.
//
// Mounting/unmounting is the activation switch. Vue's <Transition>
// removes the host element on leave, which fires `unmounted` and
// detaches the listener — no reactive bookkeeping needed.
//
// Behaviour:
//   - Tab on the LAST focusable in the host  → wraps to the first
//   - Shift+Tab on the FIRST focusable       → wraps to the last
//   - Tab when focus is OUTSIDE the host     → ignored, default Tab
//     proceeds. This lets Teleported children (e.g. a StemneSelect
//     dropdown opened from inside the modal) handle their own Tab —
//     they typically close + restore focus to their trigger inside
//     the host, and the trap re-engages from there.
//   - No focusables in the host              → Tab is swallowed so
//     focus can't escape. Edge case only — every modal in this app
//     ships with at least a close button.

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

// We attach the handler to document so capture-phase ordering is
// predictable, and stash the reference on the element itself so
// `unmounted` can find it again (no closures-via-WeakMap needed).
interface TrappedElement extends HTMLElement {
  __focusTrapHandler?: (e: KeyboardEvent) => void
}

// Filters out elements that exist in the DOM but aren't actually
// rendered (display:none, visibility:hidden ancestor, etc.).
// querySelectorAll alone returns matches by selector, not by visibility.
function isVisible(el: HTMLElement): boolean {
  return el.offsetParent !== null || el.getClientRects().length > 0
}

export const vFocusTrap: Directive<TrappedElement> = {
  mounted(el) {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      const focusables = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        isVisible,
      )

      if (focusables.length === 0) {
        e.preventDefault()
        return
      }

      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement as HTMLElement | null

      // Focus has escaped the host entirely (e.g. a Teleported child
      // unmounted on Tab and the browser dropped focus to body). Pull
      // it back into the host before letting Tab proceed — direction
      // determines whether we land on first or last.
      if (!active || !el.contains(active)) {
        e.preventDefault()
        ;(e.shiftKey ? last : first).focus()
        return
      }

      if (e.shiftKey && active === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }
    el.__focusTrapHandler = handler
    document.addEventListener('keydown', handler)
  },
  unmounted(el) {
    if (el.__focusTrapHandler) {
      document.removeEventListener('keydown', el.__focusTrapHandler)
      delete el.__focusTrapHandler
    }
  },
}
